import React, { useState, useEffect } from 'react';
import { MultiMetricOptionsProps } from '../../types';
import MultiDropDownOption from './MultiDropDownOption';
import { ReactComponent as DropDown } from '../../assets/icons/graph-table-dropdown-icon.svg';

export interface DropDownProps {
  label: string;
  data: any;
  onUpdate: (value: MultiMetricOptionsProps[]) => void;
  dropUp?: boolean;
  variant?: string;
  scrollable?: boolean;
  noOptionMessage?: string;
  tagsVisibility?: boolean;
  values?: any;
  isEditing?: boolean;
  disabled?: boolean;
  gridWidth: number;
  isTopFilter?: boolean;
  setIsDropdownOpen?: (value: React.SetStateAction<boolean>) => void;
}

const MultiDropdown: React.FC<DropDownProps> = ({
  data,
  onUpdate,
  dropUp,
  label,
  scrollable,
  variant,
  noOptionMessage,
  tagsVisibility,
  values,
  isEditing,
  disabled,
  gridWidth,
  isTopFilter,
  setIsDropdownOpen
}: DropDownProps) => {
  const [options, setOptions] = useState<MultiMetricOptionsProps[] | null>(null);
  const [allVisible, setAllVisible] = useState<boolean>(true);
  const [viewAll, setViewAll] = useState<boolean>(true);
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [visibleOptions, setVisibleOptions] = useState<MultiMetricOptionsProps[] | null>(null);
  const [callUpdate, setCallUpdate] = useState<boolean>(false);

  const ref = React.useRef(null);

  const handleClickOutside = (event: Event) => {
    if (ref.current && !(ref.current as any).contains(event.target)) {
      if (visibleOptions && visibleOptions.length === 0) {
        options?.forEach((option: MultiMetricOptionsProps) => toggleVisibility(true, option));
      }
      setShowOptions(false);
      if (setIsDropdownOpen && !isTopFilter) {
        setIsDropdownOpen(false);
      }
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [visibleOptions]);

  useEffect(() => {
    if (data) {
      const dataMetrics = variant === 'metric' ? data : [...data.values];
      const optionsFromData = dataMetrics.map((metric: any) => {
        let visible;
        if (values?.length === 0 || values?.indexOf(Number(metric.id)) !== -1) {
          visible = true;
        } else if (isEditing) {
          visible = false;
        } else {
          visible = true;
        }
        return {
          id: variant === 'metric' ? metric.dimension : metric.id,
          label: variant === 'metric' ? metric.label : metric.name,
          visible: visible
        };
      });
      setOptions(optionsFromData);
      setCallUpdate(true);
    }
  }, [data, isEditing]);

  useEffect(() => {
    if (options) {
      if (!showOptions && callUpdate) {
        onUpdate(options);
        setCallUpdate(false);
      }
      const hasAllVisibility = options.every((option) => option.visible);
      const hasAllOptionsVisibility = options
        ?.filter((o: MultiMetricOptionsProps) => o.id !== '0')
        .every((option) => option.visible);
      if (hasAllOptionsVisibility && !hasAllVisibility) {
        options?.forEach((option: MultiMetricOptionsProps) => toggleVisibility(true, option));
      }
      setAllVisible(hasAllOptionsVisibility);

      const visibleOptions = options.filter((option) => option.visible);
      setVisibleOptions(visibleOptions);
    }
    if (showOptions && setIsDropdownOpen && !isTopFilter) {
      setIsDropdownOpen(true);
    }
  }, [options, showOptions]);

  useEffect(() => {
    if (options) {
      const newArr = [...options]; // copying the old datas array
      newArr.map((metric) => (metric.visible = viewAll));
      setOptions(newArr);
      setCallUpdate(true);
    }
  }, [viewAll]);

  useEffect(() => {
    if (tagsVisibility) {
      options?.forEach((option: MultiMetricOptionsProps) => toggleVisibility(true, option));
    }
  }, [tagsVisibility]);

  const toggleVisibility = (visible: boolean, option: MultiMetricOptionsProps) => {
    if (options) {
      const newArr = [...options]; // copying the old datas array
      const item = newArr?.filter((o: MultiMetricOptionsProps) => o.id === option.id);
      item[0].visible = visible;
      const hasAllVisibility = newArr?.every((option) => option.visible);
      const allItem = newArr?.filter((o: MultiMetricOptionsProps) => o.id === '0');
      if (!hasAllVisibility && allItem[0].visible === true) {
        allItem[0].visible = visible;
      }
      const hasAllChecked = newArr
        ?.filter((o: MultiMetricOptionsProps) => o.id !== '0')
        .every((option) => option.visible);
      if (hasAllChecked && allItem[0].visible === false) {
        allItem[0].visible = true;
      }
      setOptions(newArr);
      setCallUpdate(true);
    }
  };

  return (
    <div ref={ref} className="flex items-center pr-4">
      <p className="text-xs leading-4 text-[#2b2b2b] tracking-[0.1px] uppercase p-[3px] font-nunitoBold">{label}:</p>
      {options && options.length > 0 && (
        <div className={`relative ${showOptions ? 'z-50' : 'z-20'}  w-max`}>
          <div
            className={`relative w-max cursor-pointer flex items-center justify-between bg-white box-border rounded-[4px] h-[40px] shadow-[0px 1px 2px rgba(0, 0, 0, 0.1)] ${
              gridWidth > 1050 || isTopFilter ? 'min-w-[170px]' : 'min-w-[100px]'
            } px-[13px] border-[1px] border-[rgba(0, 59, 88, 0.21)] ${disabled ? 'opacity-50' : ''}`}
            onClick={(e) => {
              if (disabled) {
                e.preventDefault();
              } else {
                setShowOptions(!showOptions);
              }
            }}
          >
            {visibleOptions && visibleOptions.length > 0 ? (
              <p className="text-[14px] font-nunitoLight pr-1">
                {allVisible ? 'ALL' : visibleOptions[0].label}{' '}
                <span className="text-[#B9B9B9]">
                  {visibleOptions.length > 1 && !allVisible ? '(+ ' + (visibleOptions.length - 1) + ' others)' : ''}
                </span>
              </p>
            ) : (
              <p className="text-[14px] font-nunitoLight">{noOptionMessage}</p>
            )}
            <DropDown />
          </div>
          {showOptions && (
            <div
              className={`absolute left-0 filter drop-shadow-dropdownFilter border border-[#e5e5ea] bg-white rounded-[2px] w-full p-[3px]
            ${dropUp ? 'bottom-[40px]' : ''}
            ${scrollable ? 'max-h-[200px] overflow-y-auto' : ''}`}
            >
              {options.map((option: MultiMetricOptionsProps) => {
                if (option.label === 'All') {
                  return (
                    <MultiDropDownOption
                      id={'all'}
                      isVisible={allVisible}
                      label={'Select All'}
                      toggleVisibility={() => {
                        if (!allVisible && viewAll) {
                          options.forEach((option: MultiMetricOptionsProps) => toggleVisibility(true, option));
                        } else if (allVisible && viewAll) {
                          options.forEach((option: MultiMetricOptionsProps) => toggleVisibility(false, option));
                        } else {
                          setViewAll(!viewAll);
                        }
                      }}
                    />
                  );
                }
                return (
                  <MultiDropDownOption
                    key={option.id}
                    id={option.id}
                    label={option.label}
                    isVisible={option.visible}
                    toggleVisibility={(value) => toggleVisibility(value, option)}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiDropdown;
